/* eslint-disable camelcase */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { t } from 'i18next';
import * as yup from 'yup';

import { IEnergyEmission } from '../../../../../../../../hooks/api/energy-emissions';
import { ALLOWED_INPUT_PRODUCT_MASS_UNITS } from '../../../../../../../../shared/helpers/product-mass-helpers';
import { DeclaredUnit } from '../../../../../../../../shared/interfaces/IComponentDetails';
import { MainRoute, PartialFullPcf } from '../../../../../../../../shared/interfaces/IFootprint';
import {
  ENERGY_SOURCE,
  IMachine,
  IWasteTreatment,
  MachineType,
  OperationMode,
  PROCESS_DROPDOWN_OPTIONS,
  ProcessIdsMap,
  SprueTreatmentSelection,
} from './PcfWizard.type';

const requiredMessage = (field: string) => {
  return t(
    `calculationWizard.sections.generalInformationSection.inputs.errorMessages.${field}Required`,
  );
};

const maxErrorMessage = (field: string, max: number) => {
  return t(
    `calculationWizard.sections.generalInformationSection.inputs.errorMessages.${field}Max${max}Error`,
  );
};

const nonPositiveError = (field: string) => {
  return t(
    `calculationWizard.sections.generalInformationSection.inputs.errorMessages.${field}NonPositiveError`,
  );
};

const MachineSchema = yup.object().shape({
  type: yup.string().oneOf(Object.values(MachineType)).required(),
});

const InjectionMoldingSchema = MachineSchema.concat(
  yup.object().shape({
    name: yup.string().when('type', (type, schema) => {
      return type?.[0] === MachineType.AUXILIARY
        ? schema
            .required(requiredMessage('machineName'))
            .max(100, maxErrorMessage('machineName', 100))
        : schema.nullable();
    }),
    operation_mode: yup
      .string()
      .oneOf(Object.values(OperationMode))
      .when('type', (type, schema) => {
        return type?.[0] === MachineType.AUXILIARY
          ? schema.required(requiredMessage('operationMode'))
          : schema.nullable();
      }),
    manufacturer_id: yup.number().when('type', (type, schema) => {
      return type?.[0] === MachineType.MAIN
        ? schema.required(requiredMessage('manufacturer'))
        : schema.nullable();
    }),
    model: yup.string().when('type', (type, schema) => {
      return type?.[0] === MachineType.MAIN
        ? schema.required(requiredMessage('model')).max(100, maxErrorMessage('model', 100))
        : schema.nullable();
    }),
    cycle_time: yup
      .number()
      .positive(nonPositiveError('cycleTime'))
      .when(['type'], (values, schema) => {
        const [type] = values;

        if (type === MachineType.MAIN) {
          return schema
            .max(3600, maxErrorMessage('cycleTime', 3600))
            .required(requiredMessage('cycleTime'));
        }

        return schema.nullable();
      }),
    operation_value: yup
      .number()
      .positive(nonPositiveError('handlingTime'))
      .when(['type', 'operation_mode'], (values, schema) => {
        const [type, operation_mode] = values;
        if (type === MachineType.AUXILIARY && operation_mode === OperationMode.HANDLING_TIME) {
          return schema
            .min(0)
            .positive(nonPositiveError('handlingTime'))
            .required(requiredMessage('handlingTime'));
        }

        return schema.nullable();
      }),
    rated_power: yup
      .number()
      .positive(nonPositiveError('ratedPower'))
      .required(requiredMessage('ratedPower')),
    number_of_cavities: yup
      .number()
      .positive(nonPositiveError('numberOfCavities'))
      .when('type', (type, schema) => {
        return type?.[0] === MachineType.MAIN
          ? schema.required(requiredMessage('numberOfCavities'))
          : schema.nullable();
      }),
    shot_weight: yup
      .number()
      .positive(nonPositiveError('shotWeight'))
      .when('type', (type, schema) => {
        return type?.[0] === MachineType.MAIN
          ? schema.required(requiredMessage('shotWeight'))
          : schema.nullable();
      }),
  }),
);

const IndividualProcessSchema = MachineSchema.concat(
  yup.object().shape({
    name: yup
      .string()
      .required(requiredMessage('machineName'))
      .max(100, maxErrorMessage('machineName', 100)),
    energy_source: yup
      .string()
      .oneOf(
        Object.values(ENERGY_SOURCE),
        t(
          'calculationWizard.sections.generalInformationSection.inputs.errorMessages.energySourceTypeError',
          { energySource: Object.values(ENERGY_SOURCE) },
        ),
      )
      .required(requiredMessage('energySource')),
    amount: yup
      .number()
      .positive(nonPositiveError('energyPerPiece'))
      .required(requiredMessage('energyPerPiece')),
    comment: yup.string().max(1000, maxErrorMessage('comment', 1000)).optional(),
    quality_assessment: yup.string(),
  }),
);

export const getSchema = () => {
  return yup.object().shape({
    declared_unit: yup
      .string()
      .oneOf(Object.values(DeclaredUnit))
      .test(function (value) {
        if (value != null) {
          const { unitary_product_amount } = this.parent;

          if (
            value === DeclaredUnit.piece &&
            unitary_product_amount &&
            unitary_product_amount !== 1
          ) {
            return this.createError({
              message: t(
                'calculationWizard.sections.generalInformationSection.inputs.errorMessages.declaredUnitMustBePieceError',
              ),
            });
          }
        }

        return true;
      })
      .required(requiredMessage('declaredUnit')),
    unitary_product_amount: yup
      .number()
      .positive(nonPositiveError('declaredAmount'))
      .when('declared_unit', (declaredUnit, schema) => {
        if (declaredUnit[0] === DeclaredUnit.piece) {
          // If declared unit is piece, the amount must be exactly 1
          return schema.test(
            'must-be-one-for-piece',
            t(
              'calculationWizard.sections.generalInformationSection.inputs.errorMessages.declaredAmountMustBeOneForPieceError',
            ),
            (value) => {
              return value === 1;
            },
          );
        }

        return schema;
      })
      .required(requiredMessage('declaredAmount')),
    product_mass_per_declared_unit: yup
      .number()
      .when('declared_unit', (declaredUnit, schema: yup.NumberSchema) => {
        if (ALLOWED_INPUT_PRODUCT_MASS_UNITS.includes(declaredUnit?.[0])) {
          return schema
            .positive(nonPositiveError('productMass'))
            .required(requiredMessage('productMass'));
        }

        return schema.nullable();
      }),
    partial_full_pcf: yup.string().oneOf(Object.values(PartialFullPcf)).required(),
    reference_period: yup.date().required(requiredMessage('referencePeriod')),
    use_of_energy: yup.mixed<IEnergyEmission>().required(requiredMessage('useOfEnergy')),
    processes: yup
      .array()
      .of(
        yup.object().shape({
          process_id: yup.string().oneOf(Object.keys(PROCESS_DROPDOWN_OPTIONS)).required(),
          name: yup.string().required(requiredMessage('processName')),
          order: yup.number().min(0).required(),
          route_name: yup.string().when('process_id', (process_id, schema: yup.StringSchema) => {
            if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
              return schema
                .required(requiredMessage('routeName'))
                .max(100, maxErrorMessage('routeName', 100));
            }

            return schema.nullable();
          }),
          main_route: yup
            .string()
            .oneOf(Object.values(MainRoute))
            .when('process_id', (process_id, schema: yup.StringSchema) => {
              if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
                return schema.required(requiredMessage('mainRoute'));
              }

              return schema.nullable();
            }),
          product_weight: yup
            .number()
            .when('process_id', (process_id, schema: yup.NumberSchema) => {
              if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
                return schema
                  .required(requiredMessage('productWeight'))
                  .positive(nonPositiveError('productWeight'));
              }

              return schema.nullable();
            }),
          route_calculation: yup
            .object()
            .shape({
              total_emissions: yup.number().optional(),
              distance: yup.number().optional(),
            })
            .optional()
            .when('process_id', (process_id, schema) => {
              if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
                return schema
                  .shape({
                    total_emissions: yup.number().required(requiredMessage('routeCalculation')),
                    distance: yup.number().required(requiredMessage('routeCalculation')),
                  })
                  .required(requiredMessage('routeCalculation'));
              }

              return schema.nullable();
            }),
          starting_point: yup
            .object()
            .typeError(requiredMessage('startingPoint'))
            .shape({
              name: yup.string().optional(),
              coordinates: yup
                .object()
                .shape({
                  longitude: yup.number().optional(),
                  latitude: yup.number().optional(),
                })
                .optional(),
            })
            .optional()
            .when('process_id', (process_id, schema) => {
              if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
                return schema.required(requiredMessage('startingPoint')).shape({
                  name: yup.string().required(requiredMessage('startingPoint')),
                  coordinates: yup
                    .object()
                    .typeError(requiredMessage('startingPoint'))
                    .shape({
                      longitude: yup.number().required(requiredMessage('startingPoint')),
                      latitude: yup.number().required(requiredMessage('startingPoint')),
                    })
                    .required(requiredMessage('startingPoint')),
                });
              }

              return schema.nullable();
            }),
          end_location: yup
            .object()
            .typeError(requiredMessage('endLocation'))
            .shape({
              name: yup.string().optional(),
              coordinates: yup
                .object()
                .shape({
                  longitude: yup.number().optional(),
                  latitude: yup.number().optional(),
                })
                .optional(),
            })
            .optional()
            .when('process_id', (process_id, schema) => {
              if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.TRANSPORT_PROCESS]) {
                return schema
                  .shape({
                    name: yup.string().required(requiredMessage('endLocation')),
                    coordinates: yup
                      .object()
                      .typeError(requiredMessage('endLocation'))
                      .shape({
                        longitude: yup.number().required(requiredMessage('endLocation')),
                        latitude: yup.number().required(requiredMessage('endLocation')),
                      })
                      .required(requiredMessage('endLocation')),
                  })
                  .required(requiredMessage('endLocation'));
              }

              return schema.nullable();
            }),
          machines: yup.array().when('process_id', (process_id, schema) => {
            const processOption = process_id?.[0] as PROCESS_DROPDOWN_OPTIONS;
            switch (processOption) {
              case ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]:
                return schema.of(InjectionMoldingSchema);
              case ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]:
                return schema.of(IndividualProcessSchema);
              default:
                return schema.of(MachineSchema);
            }
          }),

          input_materials: yup.array().when('process_id', (process_id, schema) => {
            if (process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]) {
              return schema
                .test(
                  'input-material-required',
                  requiredMessage('inputMaterial'),
                  function (value) {
                    // If it is null/undefined or empty, it's invalid
                    return value != null && value.length > 0;
                  },
                )
                .test('input-materials-injection-molding-conditional', function (value) {
                  // All items must be valid (not null/undefined)
                  // Create errors for each invalid index
                  const errors = [];
                  for (let i = 0; i < (value?.length || 1); i++) {
                    if (value?.[i] == null) {
                      errors.push(
                        this.createError({
                          path: `${this.path}[${i}]`,
                          message: requiredMessage('inputMaterial'),
                        }),
                      );
                    }
                  }

                  if (errors.length > 0) {
                    throw new yup.ValidationError(errors);
                  }

                  return true;
                });
            } else if (
              process_id?.[0] === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS]
            ) {
              return schema.test(
                'input-materials-individual-process-conditional',
                requiredMessage('inputMaterial'),
                function (value) {
                  // For Individual Process, if input_materials undefined, skip validation
                  if (value === undefined) {
                    return true;
                  }

                  // If defined (present), all items must be valid (not null/undefined)
                  const errors = [];
                  for (let i = 0; i < (value?.length || 1); i++) {
                    if (value?.[i] == null) {
                      errors.push(
                        this.createError({
                          path: `${this.path}[${i}]`,
                          message: requiredMessage('inputMaterial'),
                        }),
                      );
                    }
                  }

                  if (errors.length > 0) {
                    throw new yup.ValidationError(errors);
                  }

                  return true;
                },
              );
            } else {
              return schema.nullable();
            }
          }),
          waste_management: yup
            .object()
            .shape({
              sprue_weight: yup.number().optional(),
              sprue_treatment_type: yup
                .string()
                .oneOf(Object.values(SprueTreatmentSelection))
                .optional(),
              sprue_waste_treatment_id: yup.number().optional(),
              scrap_waste_treatment_id: yup.number().optional(),
              scrap_rate: yup.number().optional(),
              waste_treatment: yup.mixed<IWasteTreatment>().nullable(),
              amount_per_piece: yup.number().nullable(),
              waste_per_piece: yup.number().nullable(),
            })
            .optional()
            .when(['input_materials', 'process_id'], (values, schema) => {
              const [input_materials, process_id] = values;

              if (process_id === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]) {
                return schema.shape({
                  sprue_weight: yup
                    .number()
                    .required(requiredMessage('sprueWeight'))
                    .min(0, nonPositiveError('sprueWeight')),
                  sprue_treatment_type: yup
                    .string()
                    .oneOf(Object.values(SprueTreatmentSelection))
                    .optional()
                    .when(['sprue_weight'], (values, schema) => {
                      const [sprue_weight] = values;
                      if (sprue_weight > 0) {
                        return yup
                          .string()
                          .oneOf(
                            Object.values(SprueTreatmentSelection),
                            t(
                              'calculationWizard.sections.generalInformationSection.inputs.errorMessages.sprueTreatmentTypeError',
                              { sprueTreatmentType: Object.values(SprueTreatmentSelection) },
                            ),
                          )
                          .required(requiredMessage('sprueTreatmentType'));
                      }

                      return schema.nullable();
                    }),
                  sprue_waste_treatment: yup
                    .mixed<IWasteTreatment>()
                    .when(['sprue_weight', 'sprue_treatment_type'], (values, schema) => {
                      const [sprue_weight, sprue_treatment_type] = values;
                      if (
                        sprue_weight > 0 &&
                        sprue_treatment_type === SprueTreatmentSelection.DISCARDED
                      ) {
                        return schema.required(requiredMessage('sprueWasteTreatment'));
                      }

                      return schema.nullable();
                    }),
                  scrap_waste_treatment: yup
                    .mixed<IWasteTreatment>()
                    .required(requiredMessage('scrapWasteTreatment')),
                  scrap_rate: yup
                    .number()
                    .lessThan(100, maxErrorMessage('scrapRate', 100))
                    .required(requiredMessage('scrapRate'))
                    .moreThan(0, nonPositiveError('scrapRate')),
                });
              }

              if (
                process_id === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INDIVIDUAL_PROCESS] &&
                input_materials?.filter(Boolean)?.length > 0
              ) {
                return schema.shape({
                  amount_per_piece: yup
                    .number()
                    .required(requiredMessage('amountPerPiece'))
                    .positive(nonPositiveError('amountPerPiece')),
                  waste_per_piece: yup
                    .number()
                    .required(requiredMessage('wastePerPiece'))
                    .min(0, nonPositiveError('wastePerPiece')),
                  waste_treatment: yup
                    .mixed<IWasteTreatment>()
                    .when(['waste_per_piece'], (values, schema) => {
                      const [waste_per_piece] = values;
                      if (waste_per_piece > 0) {
                        return schema.required(requiredMessage('wasteTreatment'));
                      }

                      return schema.nullable();
                    }),
                });
              }

              return schema.nullable();
            }),
        }),
      )
      .test(function (processes) {
        let processCount = 0;
        for (let processIndex = 0; processIndex < (processes?.length || 0); processIndex++) {
          const process = processes?.[processIndex];
          processCount++;
          const machines: IMachine[] = process?.machines ?? [];
          if (process?.process_id === ProcessIdsMap[PROCESS_DROPDOWN_OPTIONS.INJECTION_MOLDING]) {
            const mainMachineCycleTime = machines?.find((item) => {
              return item.type && item.type === MachineType.MAIN;
            })?.cycle_time;
            if (mainMachineCycleTime) {
              for (let i = 0; i < machines.length; i++) {
                const machine = machines[i];
                if (
                  machine.type === MachineType.AUXILIARY &&
                  machine.operation_mode === OperationMode.HANDLING_TIME &&
                  machine.operation_value &&
                  machine.operation_value > mainMachineCycleTime
                ) {
                  return this.createError({
                    path: `processes[${processIndex}].machines[${i}].operation_value`,
                    message: t(
                      'calculationWizard.sections.generalInformationSection.inputs.errorMessages.handlingTimeMaxError',
                    ),
                  });
                }
              }
            }
          }
        }

        if (processCount === 0) {
          return this.createError({
            path: 'processes',
            message: t(
              'calculationWizard.sections.generalInformationSection.inputs.errorMessages.processRequired',
            ),
          });
        }

        return true;
      }),
  });
};
